// Les valeurs d'énumération et les codes restent en anglais côté API :
// leurs libellés d'affichage sont traduits ici.

// Appliquées via StatusBadge / AlertBanner : `chip`. Les fonds sont volontairement
// translucides (teinte 500 à 10 %) pour rester lisibles sur les surfaces sombres.
export const TONES = {
  success: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400',
  warning: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400',
  danger: 'bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400',
  info: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-500/10 dark:text-cyan-400',
  neutral: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  brand: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300'
}

const e = (label, tone = 'neutral') => ({ label, tone })

export const ENUMS = {
  activeFlag: { true: e('Actif', 'success'), false: e('Inactif', 'neutral') },
  vehicleStatus: {
    active: e('Actif', 'success'),
    maintenance: e('En maintenance', 'warning'),
    inactive: e('Inactif', 'neutral')
  },
  driverStatus: {
    active: e('Actif', 'success'),
    suspended: e('Suspendu', 'warning'),
    inactive: e('Inactif', 'neutral')
  },
  paymentStatus: {
    pending: e('En attente', 'warning'),
    validated: e('Validé', 'success'),
    rejected: e('Rejeté', 'danger')
  },
  paymentMethod: {
    cash: e('Espèces'),
    mobile_money: e('Mobile money'),
    bank_transfer: e('Virement'),
    other: e('Autre')
  },
  reportStatus: {
    pending: e("En file d'attente", 'neutral'),
    processing: e('En cours', 'info'),
    completed: e('Terminé', 'success'),
    failed: e('Échec', 'danger')
  },
  reportType: {
    reconciliation: e('Rapprochement'),
    revenues: e('Recettes'),
    expenses: e('Dépenses'),
    payments: e('Versements'),
    fuel: e('Carburant')
  },
  reportFormat: { csv: e('CSV'), pdf: e('PDF') },
  dueStatus: {
    overdue: e('En retard', 'danger'),
    due_soon: e('Bientôt dû', 'warning'),
    ok: e('À jour', 'success')
  },
  role: {
    admin: e('Administrateur', 'brand'),
    gestionnaire: e('Gestionnaire', 'info'),
    comptable: e('Comptable', 'success'),
    proprietaire: e('Propriétaire', 'warning'),
    chauffeur: e('Chauffeur')
  },
  auditEvent: {
    created: e('Création', 'success'),
    updated: e('Modification', 'info'),
    deleted: e('Suppression', 'danger'),
    restored: e('Restauration', 'warning'),
    login: e('Connexion'),
    logout: e('Déconnexion'),
    logout_all: e('Déconnexion (tous appareils)'),
    password_changed: e('Mot de passe modifié', 'warning'),
    password_reset: e('Mot de passe réinitialisé', 'warning')
  },
  auditableType: {
    user: e('Utilisateur'),
    owner: e('Propriétaire'),
    driver: e('Chauffeur'),
    driver_assignment: e('Affectation'),
    vehicle_type: e('Type de véhicule'),
    vehicle: e('Véhicule'),
    revenue: e('Recette'),
    expense_category: e('Catégorie de dépense'),
    expense: e('Dépense'),
    payment: e('Versement'),
    fuel_log: e('Plein'),
    maintenance_plan: e('Plan de maintenance'),
    maintenance_record: e('Intervention'),
    organization: e('Organisation')
  }
}

export function enumLabel(name, value) {
  return ENUMS[name]?.[value]?.label ?? value ?? '—'
}

export function enumTone(name, value) {
  return ENUMS[name]?.[value]?.tone ?? 'neutral'
}

export function enumOptions(name) {
  return Object.entries(ENUMS[name]).map(([value, { label }]) => ({ value, label }))
}

/** Libellés des permissions (resource.action) affichées dans la page Rôles. */
export const PERMISSION_GROUPS = {
  users: 'Utilisateurs',
  roles: 'Rôles',
  owners: 'Propriétaires',
  drivers: 'Chauffeurs',
  vehicle_types: 'Types de véhicules',
  vehicles: 'Véhicules',
  revenues: 'Recettes',
  expense_categories: 'Catégories de dépenses',
  expenses: 'Dépenses',
  payments: 'Versements',
  reconciliation: 'Rapprochement',
  fuel_logs: 'Carburant',
  maintenance_plans: 'Plans de maintenance',
  maintenance_records: 'Interventions',
  dashboard: 'Tableau de bord',
  reports: 'Rapports',
  audit: "Journal d'audit"
}

export const PERMISSION_ACTIONS = {
  view: 'Consulter',
  create: 'Créer',
  update: 'Modifier',
  delete: 'Supprimer',
  validate: 'Valider',
  assign: 'Affecter'
}

const VOWEL = /^[aeiouyhéèêâîôû]/i

/** « Nouveau véhicule », « Nouvelle recette », « Nouvel utilisateur » (accord et élision). */
export function newLabel(config) {
  if (config.createLabel) return config.createLabel
  const word = config.feminine ? 'Nouvelle' : VOWEL.test(config.singular) ? 'Nouvel' : 'Nouveau'
  return `${word} ${config.singular}`
}

/** « ce véhicule », « cette recette », « cet utilisateur ». */
export function demonstrative(config) {
  if (config.feminine) return 'cette'
  return VOWEL.test(config.singular) ? 'cet' : 'ce'
}
